import { theme } from "@polifonia/theme";
import dayjs from "dayjs";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import { NumberFormat } from "@/components/NumberFormat";
import { RouterLink } from "@/components/RouterLink";
import { Typography } from "@/components/Typography";
import { summarizeActiveContacts } from "@/features/platformStatus/activeContacts";
import { interlude } from "@/interlude";
import { SummaryCard } from "@/pages/components/SummaryCard";

/** Signed, so a rise reads "+200" and a fall "-200". */
const changeFormat = new Intl.NumberFormat(undefined, {
  signDisplay: "exceptZero",
});

const reportingDayFormat = new Intl.DateTimeFormat("en-CA", {
  timeZone: "Asia/Taipei",
  year: "numeric",
  month: "2-digit",
  day: "2-digit",
});

/**
 * Contacts active in the last 30 days, de-duplicated across channels, with the
 * change from 30 days earlier. The same daily figure the billing overview
 * charts; callers show it only where that chart is enabled.
 */
export const ActiveContactsCard: React.FC = function ActiveContactsCard() {
  const { t } = useTranslation();
  const dateRange = useMemo(() => {
    const today = dayjs(reportingDayFormat.format(new Date()));
    return {
      dateFrom: today.subtract(31, "day").format("YYYY-MM-DD"),
      dateTo: today.format("YYYY-MM-DD"),
    };
  }, []);
  const uniqueContactsQuery =
    interlude.uniqueContacts.useGetUniqueContactsOverview({
      queries: dateRange,
    });
  const summary = useMemo(
    () =>
      uniqueContactsQuery.data
        ? summarizeActiveContacts(uniqueContactsQuery.data.records)
        : null,
    [uniqueContactsQuery.data],
  );

  if (!summary) return null;

  return (
    <SummaryCard
      title={t("billing.uniqueContacts.title")}
      action={
        <RouterLink to="/billing">
          {t("overview.attention.billing.action", "Go to billing")}
        </RouterLink>
      }
    >
      <Typography variant="value">
        <NumberFormat value={summary.count} />
      </Typography>
      {summary.change === null ? null : (
        <Typography variant="note" color={theme.colors.staticFgNote}>
          {t("overview.activeContacts.change", {
            change: changeFormat.format(summary.change),
            defaultValue: "{{change}} vs. 30 days earlier",
          })}
        </Typography>
      )}
      <Typography variant="note" color={theme.colors.staticFgNote}>
        {t("billing.uniqueContacts.dataThrough", { date: summary.date })}
      </Typography>
    </SummaryCard>
  );
};
